import React from 'react';
import { Box, Tooltip, useTheme } from '@mui/material';

export type ClipOutputMode = 'portrait' | 'landscape';

/**
 * Figma's `base/cosm-black`. The theme's `baseblack` token is #000000, which is a
 * different colour, so this follows the same literal already used elsewhere for
 * dark glyphs on a white pill (see PlayerCameraFeedSelectionButton).
 */
const COSM_BLACK = '#09090C';

/**
 * Aspect-ratio glyphs, traced from the Figma export (Game View, node 5605:46722).
 * Both are the same 8.2 x 15.2 rounded rect (rx 1.6) plus a speaker slot; the
 * landscape variant is the rect rotated 90 degrees. Strokes use currentColor so
 * the glyph follows the button's selected/unselected colour.
 */
const LandscapeGlyph: React.FC = () => (
  <svg
    width="16"
    height="10.8"
    viewBox="0 0 16 10.8"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <rect
      x="15.6"
      y="0.4"
      width="8.2"
      height="15.2"
      rx="1.6"
      transform="rotate(90 15.6 0.4)"
      stroke="currentColor"
      strokeWidth="0.8"
    />
    <path d="M6 10.5L10 10.5" stroke="currentColor" strokeWidth="0.6" strokeLinecap="round" />
  </svg>
);

const PortraitGlyph: React.FC = () => (
  <svg width="9" height="16" viewBox="0 0 9 16" fill="none" aria-hidden="true" focusable="false">
    <rect
      x="0.4"
      y="0.4"
      width="8.2"
      height="15.2"
      rx="1.6"
      stroke="currentColor"
      strokeWidth="0.8"
    />
    <path d="M3 1.75L6 1.75" stroke="currentColor" strokeWidth="0.5" strokeLinecap="round" />
  </svg>
);

interface AspectRatioOptionProps {
  mode: ClipOutputMode;
  label: string;
  selected: boolean;
  disabled: boolean;
  onSelect: (mode: ClipOutputMode) => void;
}

const AspectRatioOption: React.FC<AspectRatioOptionProps> = ({
  mode,
  label,
  selected,
  disabled,
  onSelect,
}) => {
  const theme = useTheme();
  const palette = theme.palette.cx;

  return (
    <Tooltip title={label}>
      {/* span keeps the tooltip working while the button is disabled */}
      <span style={{ display: 'inline-flex' }}>
        <Box
          component="button"
          type="button"
          aria-pressed={selected}
          aria-label={label}
          disabled={disabled}
          onClick={() => onSelect(mode)}
          sx={{
            width: 28,
            height: 28,
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            // 0.875px stroke in the design; MUI rounds sub-pixel borders consistently.
            border: `0.875px solid ${selected ? palette.text.primary : palette.text.muted}`,
            backgroundColor: selected ? palette.text.primary : 'transparent',
            color: selected ? COSM_BLACK : palette.text.muted,
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
              duration: theme.transitions.duration.shortest,
            }),
            '&:hover:not(:disabled)': {
              borderColor: selected ? palette.text.primary : palette.text.tertiary,
              color: selected ? COSM_BLACK : palette.text.tertiary,
            },
            '&:focus-visible': {
              outline: `2px solid ${palette.blue.hover}`,
              outlineOffset: 2,
            },
          }}
        >
          {/* 20x20 icon frame from the design, glyph centred inside it */}
          <Box
            sx={{
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {mode === 'portrait' ? <PortraitGlyph /> : <LandscapeGlyph />}
          </Box>
        </Box>
      </span>
    </Tooltip>
  );
};

interface ClipAspectRatioToggleProps {
  /** Currently selected output mode. */
  value: ClipOutputMode;
  /** Called with the newly picked mode. Not called when the mode is unchanged. */
  onChange: (mode: ClipOutputMode) => void;
  disabled?: boolean;
}

/**
 * Segmented 16:9 / 9:16 picker for a clip group's export aspect ratio (C360-3092).
 * Both options stay visible; the active one is filled white, matching the design.
 */
export const ClipAspectRatioToggle: React.FC<ClipAspectRatioToggleProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const handleSelect = (mode: ClipOutputMode) => {
    if (mode !== value) {
      onChange(mode);
    }
  };

  return (
    <Box
      role="group"
      aria-label="Clip export aspect ratio"
      sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}
    >
      <AspectRatioOption
        mode="landscape"
        label="Landscape 16:9"
        selected={value === 'landscape'}
        disabled={disabled}
        onSelect={handleSelect}
      />
      <AspectRatioOption
        mode="portrait"
        label="Portrait 9:16"
        selected={value === 'portrait'}
        disabled={disabled}
        onSelect={handleSelect}
      />
    </Box>
  );
};

export default ClipAspectRatioToggle;
